/**
 * Resize and compress an image file using the Canvas API.
 *
 * @param {File} file - The source image file
 * @param {object} opts
 * @param {number} [opts.maxWidth]   - Max output width in px (optional)
 * @param {number} [opts.maxHeight]  - Max output height in px (optional)
 * @param {number} [opts.quality]    - JPEG quality 0–1 (default 0.85, ignored for PNG)
 * @returns {Promise<{ blob: Blob, fileName: string, contentType: string }>}
 */
export function resizeImage(file, { maxWidth, maxHeight, quality = 0.85 } = {}) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const blobUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(blobUrl);

      let { width, height } = img;

      // Scale down to fit within maxWidth / maxHeight, never upscale
      const scaleX = maxWidth ? maxWidth / width : 1;
      const scaleY = maxHeight ? maxHeight / height : 1;
      const scale = Math.min(scaleX, scaleY, 1);
      width = Math.round(width * scale);
      height = Math.round(height * scale);

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);

      // Keep PNG (preserves transparency), convert everything else to JPEG
      const contentType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
      const ext = contentType === 'image/png' ? 'png' : 'jpg';
      const baseName = file.name.replace(/\.[^.]+$/, '');
      const fileName = `${baseName}.${ext}`;

      canvas.toBlob(
        (blob) => resolve({ blob, fileName, contentType }),
        contentType,
        quality,
      );
    };

    img.onerror = reject;
    img.src = blobUrl;
  });
}
